import React, { useRef, useState, useEffect } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { heroContent } from "../mock";
import { HighlightText } from "../components/ui/animated-reveal-text";
import { useModeContext } from "../contexts/ModeContext";

gsap.registerPlugin(useGSAP);

const VideoSection = () => {
  const { activeMode } = useModeContext();
  const root = useRef(null);
  const ringRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isUnmuted, setIsUnmuted] = useState(false);
  const [scale, setScale] = useState(1);

  // Auto-scaling based on viewport size
  useEffect(() => {
    const updateScale = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Base scale calculation
      let newScale = 1;
      
      // Scale down on smaller screens
      if (viewportWidth < 768) {
        newScale = Math.max(0.85, Math.min(1, viewportWidth / 768));
      } else if (viewportWidth < 1024) {
        newScale = Math.max(0.9, Math.min(1, viewportWidth / 1024));
      } else if (viewportWidth > 1920) {
        // Scale up slightly on very large screens
        newScale = Math.min(1.1, 1 + (viewportWidth - 1920) / 5000);
      }
      
      // Adjust based on height if needed
      if (viewportHeight < 800) {
        newScale = Math.min(newScale, Math.max(0.8, viewportHeight / 800));
      }
      
      setScale(newScale);
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  useGSAP(
    () => {
      const ctx = gsap.context(() => {
        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
        tl.from(".video-title", { y: 30, opacity: 0, duration: 0.7 })
          .from(".video-image", { x: -40, opacity: 0, duration: 0.8 }, "-0.2")
          .from(".video-text", { y: 20, opacity: 0, duration: 0.6 }, "-0.2")
          .from(".video-quote", { y: 18, opacity: 0, duration: 0.6 }, "-0.3");
        gsap.to(ringRef.current, { rotate: 360, duration: 18, repeat: -1, ease: "none", transformOrigin: "50% 50%" });
      }, root);
      return () => ctx.revert();
    },
    { scope: root }
  );

  const c = heroContent.colors;

  // Ensure background video auto-plays inline and is muted by default
  // Also reload video when mode changes
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const play = () => v.play().catch(() => {});
    if (v.readyState >= 2) play();
    else v.addEventListener("canplay", play, { once: true });
    return () => v.removeEventListener("canplay", play);
  }, [activeMode]); // Re-run when activeMode changes

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    if (!isUnmuted) {
      v.muted = false;
      v.currentTime = 0;
      v.play().catch(() => {});
      setIsUnmuted(true);
    } else {
      v.muted = true;
      v.currentTime = 0;
      v.play().catch(() => {});
      setIsUnmuted(false);
    }
  };

  return (
    <section id="coaching" ref={root} className={`relative w-full h-auto md:h-auto md:min-h-[100svh] pt-[90px] md:pt-[96px] pb-6 md:pb-12 bg-white dark:bg-[#0B0B0C] overflow-visible ${activeMode === 1 ? 'z-[100]' : 'z-50'}`} style={{ transform: 'scale(1)', transformOrigin: 'center' }}>
      <div className="mx-auto max-w-7xl px-6 h-auto md:h-full flex flex-col" ref={containerRef} style={{ 
        transform: `scale(${scale})`,
        transformOrigin: 'center',
        transition: 'transform 0.3s ease-out'
      }}>
        {/* Large heading "VIDEO" */}
                <h2 
          className="video-title whitespace-nowrap text-[clamp(2rem,9vw,7rem)] font-semibold tracking-[-0.04em] leading-[0.95] select-none"
          style={{ color: activeMode === 0 ? '#A9BBB5' : 'rgb(229 229 229 / var(--tw-text-opacity, 1))' }}
        >
          {activeMode === 0 ? "Was dich erwartet" : "Der Ansatz"}
        </h2>
        <div className="mt-1">
          <HighlightText
            as="p"
            text={"Authentisch und praxisnah."}
            textClassName={`text-[clamp(0.95rem,2.2vw,1.1rem)] font-[600] ${activeMode === 0 ? 'text-white' : 'text-[#161616]'}`}
            highlightClassName={`rounded-md ${activeMode === 1 ? 'shine-loop' : ''}`}
            highlightColor="var(--brand-accent)"
            shineOnHover
            shineColor="rgba(255,255,255,0.55)"
            duration={1.0}
            ease="easeInOut"
            organic={false}
          />
        </div>

        {/* Intro text */}
        <div className="mt-6 mb-6 p-4 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 max-w-2xl">
          <p className="text-base text-neutral-700 dark:text-neutral-300 leading-relaxed">
            {activeMode === 0 
              ? "Meine Arbeitsweise und Philosophie."
              : "Es geht nicht nur um Business: Der Blick richtet sich auch auf Beziehungen, Gesundheit, Körper und Einfluss. Echte Spitzenleistung beginnt mit innerer Ausrichtung."
            }
          </p>
        </div>

        <div className="mt-3 md:mt-4 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" style={{
          transform: 'scale(1)',
          transformOrigin: 'center',
          width: '100%',
          maxWidth: '100%'
        }}>
          {/* Inline background video with play/stop toggle */}
          <div className="lg:col-span-6" style={{ 
            width: '100%',
            maxWidth: 'clamp(280px, 90vw, 640px)',
            margin: '0 auto'
          }}>
            <div className={`video-image relative w-full aspect-[16/10] rounded-xl overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.10)] ${activeMode === 1 ? 'z-[110]' : 'z-[60]'}`} style={{
              maxWidth: '100%',
              height: 'auto'
            }}>
              <video
                ref={videoRef}
                src={activeMode === 0 ? "/verhaltensaenderung2.webm" : "/peakperformance2.webm"}
                className={`w-full h-full object-cover ${activeMode === 1 ? 'relative z-[110]' : 'relative z-[60]'}`}
                muted
                autoPlay
                loop
                playsInline
                preload="auto"
              />
              <button
                onClick={toggleSound}
                className={`absolute right-4 ${activeMode === 1 ? 'bottom-4 md:bottom-8' : 'bottom-4'} ${activeMode === 1 ? 'z-[120]' : 'z-[70]'} w-20 h-20 rounded-full bg-white/95 text-neutral-900 grid place-items-center shadow-md hover:shadow-lg transition`}
                aria-label={isUnmuted ? "Video stumm schalten" : "Video mit Ton abspielen"}
                aria-pressed={isUnmuted}
              >
                <svg ref={ringRef} width="100%" height="100%" viewBox="0 0 96 96" className="absolute inset-0 pointer-events-none" aria-hidden="true">
                  <defs>
                    <path id="videoTextcircle" d="M48,48 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                  </defs>
                  <text fill="#9CA3AF" fontSize="9" fontWeight="500" letterSpacing="0.5">
                    <textPath href="#videoTextcircle" startOffset="0" method="align" spacing="auto" textLength="238">
                      {isUnmuted ? 'Stop • Stop • Stop • Stop • Stop • Stop •' : 'Play • Play • Play • Play • Play • Play •'}
                    </textPath>
                  </text>
                </svg>
                <div className="w-9 h-9 rounded-full bg-neutral-900 grid place-items-center">
                  {isUnmuted ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="6" y="6" width="12" height="12" fill="white" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 5v14l11-7L8 5z" fill="white" />
                    </svg>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Text content */}
          <div className="lg:col-span-6 video-text" style={{
            width: '100%',
            maxWidth: '100%',
            fontSize: 'clamp(14px, 1.5vw, 18px)',
            paddingBottom: '2rem'
          }}>
            <p className="text-[16px] md:text-[18px] leading-[1.75] text-neutral-800 dark:text-neutral-300" style={{
              fontSize: 'clamp(14px, 1.5vw, 18px)'
            }}>
              {activeMode === 0
                ? "Beim Behaviour Change Coaching kombiniere ich fundierte psychologische Erkenntnisse mit praktischen Tools aus der Verhaltensforschung. Ob es um Leadership, Teamdynamiken oder persönliche Transformation geht – hier entsteht messbare Veränderung, die bleibt. Wir arbeiten evidenzbasiert, klar strukturiert und an konkreten Zielen. Der Transfer in deinen Alltag steht dabei im Mittelpunkt – pragmatisch, menschlich und messbar."
                : "Executive Coaching kombiniert strategische Führungsexpertise mit bewährten Performance-Methoden. Ob es um Leadership-Excellence, Team-Optimierung oder persönliche Höchstleistung geht – hier entstehen messbare Ergebnisse, die nachhaltig wirken. Der Ansatz ist evidenzbasiert, strukturiert und zielorientiert. Die Umsetzung in den Führungsalltag steht im Mittelpunkt – strategisch, professionell und messbar."
              }
            </p>

            <p
              className="video-quote mt-8 md:mt-10 mb-4 text-[21px] md:text-[24px] text-neutral-900 dark:text-white"
              style={{ 
                fontFamily: "'Caveat', cursive",
                fontSize: 'clamp(19px, 2.2vw, 24px)',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}
            >
              {activeMode === 0
                ? "Für Menschen, die nicht nur verstehen wollen, warum sie handeln, wie sie handeln – sondern die bereit sind, etwas zu tun, damit es sich verändert."
                : "Für Führungskräfte, die nicht nur erfolgreich sein wollen – sondern bereit sind, ihre Potentiale voll auszuschöpfen und nachhaltige Höchstleistung zu erreichen."
              }
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;


